import { VehicleCategory } from 'util/vehicle'

import type { TranslationKey } from 'localization'

export type Profile = {
  biography: string
  avatar_url?: string
  verified_avatar_url?: string
}

export type ProfileBasicInfo = {
  first_name: string
  middle_name: string
  last_name: string
  preferred_address: {
    street: string
    apt_number: string
    city: string
    state: string
    zip: string
  }
  ein_verifications: EINVerification[]
  certifications: any[]
}

export type LockReason = {
  compliace_region?: string
  locked_at: string
  reason: string
  subreason?: string
}

export type EINVerification = {
  ein_verification: {
    id: number
    business_name: string
    discarded_at: string
    ein: string
    state_of_incorporation: string
    status: string
    multi_member_ein: boolean
  }
}

export type DrivingProfile = {
  id: number
  certifications: string[]
  drivers_license?: {
    state?: string
    number_last_four?: string
    hashed_number?: string
    expiration_date?: string
    updated_at?: string
  }
  vehicles: any[]
}

export type ProfileVehicle = {
  details: {
    id: number
    vin: string | null
    license_plate: string | null
    license_plate_state: string | null
    color: string
    display_name: string | null
    legacy_vehicle_id: number | null
  }
  specs: {
    year: number | null
    make: string | null
    model: string | null
    category: VehicleCategory | null
  }
  insurance: {
    expiration_date: string | null
    status: string | null
  }
  selected_as_active: boolean
  plate_lookup: {
    display_name: string | null
    vin: string | null
    failure_reason: string | null
    result: 'failure' | 'success' | 'not_attempted' | 'in_conflict' | 'pending'
  }
  vin_conflicts: Array<{
    display_name: string
    type_id: number | null
    vin: string
  }> | null
}

export type AccountSetting = {
  setting: string
  value: string
  updated_at: string
  created_at: string
}

export type Flags = {
  [key: string]: boolean
}

export type DriverRoute = {
  stops: []
  route_polyline: string
}

// Certifications that we show on the profile page. See DWEB-70
// TODO: move these icons either to roadie-ui or move links to main-api
export const supportedCertifications: {
  [key: string]: {
    display_name: string
    description_key: TranslationKey
    url: string
    available_for_drivers?: boolean
    eligibility_cert?: string
    cert_link?: string
    learning_description_key?: TranslationKey
  }
} = {
  tsa_certified: {
    display_name: 'TSA Certified',
    description_key: 'dweb_certifications_description_tsa_certified',
    url: 'https://cdn.roadie.com/certifications/tsa.png',
  },
  extra_equipped: {
    display_name: 'Extra Equipped',
    description_key: 'dweb_certifications_description_extra_equipped',
    url: 'https://cdn.roadie.com/certifications/extra_equipped.png',
  },
  construction_site_ppe: {
    display_name: 'Job Site',
    description_key: 'dweb_certifications_description_construction_site_ppe',
    url: 'https://cdn.roadie.com/certifications/job_site.png',
  },
  cargo_trailer: {
    display_name: 'Trailer Required',
    description_key: 'dweb_certifications_description_cargo_trailer',
    url: 'https://cdn.roadie.com/certifications/trailer.png',
  },
  trailer_hitch: {
    display_name: 'Trailer Required (Hitch OK)',
    description_key: 'dweb_certifications_description_trailer_hitch',
    url: 'https://cdn.roadie.com/certifications/trailer.png',
  },
  ecofriendly_vehicle: {
    display_name: 'Eco-friendly Vehicle',
    description_key: 'dweb_certifications_description_ecofriendly_vehicle',
    url: 'https://cdn.roadie.com/certifications/eco-friendly.png',
  },
  mail_innovations: {
    display_name: 'Mail Innovations',
    description_key: 'dweb_certifications_description_mail_innovations',
    url: 'https://cdn.roadie.com/certifications/mail_innovations.png',
  },
  threshold_delivery: {
    display_name: 'Over-The-Threshold',
    description_key: 'dweb_certifications_description_threshold_delivery',
    url: 'https://cdn.roadie.com/certifications/over-threshold.png',
    available_for_drivers: true,
    eligibility_cert: 'threshold_delivery_eligible',
    cert_link: '/certifications/threshold',
    learning_description_key: 'dweb_certifications_learning_description_threshold_delivery',
  },
  pharmacy_gig: {
    display_name: 'Pharmacy',
    description_key: 'dweb_certifications_description_pharmacy_gig',
    url: 'https://cdn.roadie.com/certifications/pharmacy.png',
    available_for_drivers: true,
    cert_link: '/certifications/pharmacy',
    learning_description_key: 'dweb_certifications_learning_description_pharmacy_gig',
  },
  roadie_returns: {
    display_name: 'Roadie Returns',
    description_key: 'dweb_certifications_description_roadie_returns',
    url: 'https://cdn.roadie.com/certifications/returns.png',
    available_for_drivers: true,
    eligibility_cert: 'roadie_returns_eligible',
    cert_link: '/certifications/returns',
    learning_description_key: 'dweb_certifications_learning_description_roadie_returns',
  },
  roadie_xd: {
    display_name: 'RoadieXD™',
    description_key: 'dweb_certifications_description_roadie_xd',
    url: 'https://cdn.roadie.com/certifications/xd.png',
    available_for_drivers: true,
    cert_link: '/certifications/roadiexd',
    learning_description_key: 'dweb_certifications_learning_description_roadie_xd',
  },
}
